import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { supabase } from "../lib/supabaseClient";
import type { AuthUser as TAuthUser } from "../types/auth";

function isValidEmail(s: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

export type AuthUser = TAuthUser;

type AuthContextValue = {
  user: AuthUser | null;
  loading: boolean;
  setUser: React.Dispatch<React.SetStateAction<AuthUser | null>>;
  displayName: string;
  setDisplayName: (name: string) => void;
  signUp: (
    email: string,
    password: string,
  ) => Promise<{ ok: boolean; error?: string; requiresConfirmation?: boolean }>;
  signIn: (email: string) => Promise<{ ok: boolean; error?: string }>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchProfile(userId: string) {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, email, role, subscription, full_name, permissions")
    .eq("id", userId)
    .maybeSingle();
  if (error) {
    console.warn("[Auth] profiles fetch error:", error);
    return null;
  }
  return data ?? null;
}

function mergeUser(sessionUser: any, profile: any): AuthUser {
  return {
    id: sessionUser.id,
    email: sessionUser.email ?? profile?.email ?? undefined,
    name:
      sessionUser.user_metadata?.full_name ?? profile?.full_name ?? undefined,
    avatarUrl: sessionUser.user_metadata?.avatar_url ?? undefined,
    role: profile?.role ?? undefined,
    subscription: profile?.subscription ?? undefined,
    permissions: profile?.permissions ?? {},
  };
}

function deriveDisplayName(u: AuthUser | null): string {
  if (!u) return "";
  if (u.name && u.name.trim().length > 0) return u.name.trim();
  const email: string = u.email || "";
  return email.includes("@") ? email.split("@")[0] : "Guest";
}

export const AuthProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState<string>("");

  const lastLoadedUserId = useRef<string | null>(null);
  const isLoadingProfile = useRef<boolean>(false);

  const loadAndMergeProfile = useCallback(async (sessionUser: any) => {
    if (!sessionUser) return;

    if (lastLoadedUserId.current === sessionUser.id) {
      return;
    }

    if (isLoadingProfile.current) {
      return;
    }

    isLoadingProfile.current = true;

    try {
      const profile = await fetchProfile(sessionUser.id);

      if (profile) {
        lastLoadedUserId.current = sessionUser.id;

        setUser((currentUser) => {
          const merged = mergeUser(sessionUser, profile);
          setDisplayName(deriveDisplayName(merged));

          if (!currentUser || currentUser.id !== sessionUser.id) {
            return merged;
          }

          return {
            ...currentUser,
            name: profile.full_name ?? currentUser.name,
            role: profile.role ?? currentUser.role,
            subscription: profile.subscription ?? currentUser.subscription,
            permissions: profile.permissions ?? currentUser.permissions,
          };
        });
      }
    } finally {
      isLoadingProfile.current = false;
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    let authStateSubscription: any;

    const initializeAuth = async () => {
      const { data } = await supabase.auth.getSession();
      const sessionUser = data.session?.user ?? null;

      if (sessionUser) {
        const baseUser = mergeUser(sessionUser, null);

        if (isMounted) {
          setUser(baseUser);
          setDisplayName(deriveDisplayName(baseUser));
          if (lastLoadedUserId.current !== sessionUser.id) {
            await loadAndMergeProfile(sessionUser);
          }
        }
      } else {
        if (isMounted) {
          setUser(null);
          setDisplayName("");
          lastLoadedUserId.current = null;
        }
      }

      if (isMounted) setLoading(false);
    };

    initializeAuth();

    const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
      const sessionUser = session?.user ?? null;

      if (event === "SIGNED_OUT" || !sessionUser) {
        setUser(null);
        setDisplayName("");
        lastLoadedUserId.current = null;
      } else if (sessionUser && isMounted) {
        setUser((currentUser) => {
          if (currentUser?.id === sessionUser.id) {
            return currentUser;
          }

          const baseUser = mergeUser(sessionUser, null);
          setDisplayName(deriveDisplayName(baseUser));

          if (lastLoadedUserId.current !== sessionUser.id) {
            loadAndMergeProfile(sessionUser);
          }

          return baseUser;
        });
      }
    });

    authStateSubscription = data?.subscription;

    return () => {
      isMounted = false;
      if (authStateSubscription) {
        authStateSubscription.unsubscribe();
      }
    };
  }, [loadAndMergeProfile]);

  const signUp = async (email: string, password: string) => {
    if (!isValidEmail(email))
      return { ok: false, error: "Enter a valid email address." };
    if (!password || password.length < 8)
      return { ok: false, error: "Password must be at least 8 characters." };
    try {
      const redirectTo = `${window.location.origin}/auth/callback`;
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: redirectTo },
      });
      if (error) throw error;
      return { ok: true, requiresConfirmation: !data.session };
    } catch (e: any) {
      return { ok: false, error: e?.message ?? "Sign up failed." };
    }
  };

  const signIn = async (email: string) => {
    try {
      const redirectTo = `${window.location.origin}/auth/callback`;
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: { shouldCreateUser: true, emailRedirectTo: redirectTo },
      });
      if (error) return { ok: false, error: error.message };
      return { ok: true };
    } catch (e: any) {
      return { ok: false, error: e?.message ?? "Magic sign-in failed." };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setDisplayName("");
    lastLoadedUserId.current = null;
  };

  const value = useMemo(
    () => ({
      user,
      loading,
      setUser,
      displayName,
      setDisplayName,
      signIn,
      signOut,
      signUp,
    }),
    [user, loading, displayName],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx)
    throw new Error("useAuth must be used within Supabase AuthProvider");
  return ctx;
}
